import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ANNIV, crabById, crabSvg, loadPool, monthLooks } from '../../lib/clawdPool.js';
import './Calendar.css';

/* ============================================================
   蟹蟹月历（2026-11-04 铃点单）—— 入口=首页信纸卡右上的邮票
   每天一只打扮好的小蟹（蟹蟹日历原规则，和原作者的月历同一份），
   过去的日子彩色、还没到的淡淡的；纪念日换小邮差蟹送信。
   点开过去的某一天 → 那天他真做过的事（/api/studio/month，只认真实痕迹）。
   ============================================================ */

// 他们相遇的那个月之前不翻；往后最多看一年
const FIRST = 2026 * 12 + 1; // 2026-02（月份从 0 数）
const span = (y, m) => y * 12 + (m - 1);

async function fetchMonth(y, m) {
  const ym = `${y}-${String(m).padStart(2, '0')}`;
  const r = await fetch(`/api/studio/month?ym=${ym}`, { credentials: 'include' });
  if (!r.ok) throw new Error('studio/month ' + r.status);
  return r.json();
}

function Svg({ html, className }) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function Calendar() {
  const today = useMemo(() => new Date(), []);
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1;
  const td = today.getDate();

  const [pool, setPool] = useState(null);
  const [ym, setYm] = useState({ y: ty, m: tm });
  const [sel, setSel] = useState(td);
  const [days, setDays] = useState({});

  useEffect(() => {
    loadPool().then(setPool).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setDays({});
    fetchMonth(ym.y, ym.m)
      .then((d) => alive && setDays(d.days || {}))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ym.y, ym.m]);

  const looks = useMemo(() => (pool ? monthLooks(pool, ym.y, ym.m) : null), [pool, ym.y, ym.m]);
  const nDays = new Date(ym.y, ym.m, 0).getDate();
  const cur = span(ym.y, ym.m);
  const now = span(ty, tm);
  const isFuture = (d) => cur > now || (cur === now && d > td);

  const go = (delta) => {
    const t = cur + delta;
    if (t < FIRST || t > now + 12) return;
    const y = Math.floor(t / 12);
    const m = (t % 12) + 1;
    setYm({ y, m });
    setSel(y === ty && m === tm ? td : 1);
  };

  if (!pool || !looks) {
    return <div className="cal-app"><div className="cal-sheet cal-loading">小蟹蟹们在路上…</div></div>;
  }

  const look = looks[sel] && looks[sel].o;
  const anniv = ANNIV[`${ym.m}-${sel}`];
  const acts = days[String(sel)] || [];
  const selFuture = isFuture(sel);
  const selToday = cur === now && sel === td;

  return (
    <div className="cal-app">
      <div className="cal-sheet">
        {/* —— 选中的那一天：大大的一只 —— */}
        <div className="cal-hero">
          <Svg className="cal-hero-crab" html={crabSvg(pool, look, { anim: true })} />
          <div className="cal-hero-name">
            {look && look.job ? `${look.job} · ${look.name}` : '还没到'}
          </div>
          <div className="cal-hero-date">
            {ym.m} 月 {sel} 日{selToday ? ' · 今天' : ''}
            {anniv && <span className="cal-anniv">{anniv}</span>}
          </div>
        </div>

        {/* —— 那天他做了什么 —— */}
        <div className="cal-acts">
          {selFuture ? (
            <div className="cal-acts-empty">这一天还没到，小蟹先替他占好位置。</div>
          ) : acts.length ? (
            <>
              <div className="cal-acts-t">{selToday ? '今天他做了' : '那天他做了'}</div>
              <div className="cal-acts-row">
                {acts.map((a) => (
                  <div className="cal-act" key={a.key}>
                    <Svg className="cal-act-crab" html={crabSvg(pool, crabById(pool, a.crab), { anim: false, label: a.say })} />
                    <span className="cal-act-t">{a.say}</span>
                    {a.n > 1 && <span className="cal-act-n">× {a.n}</span>}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="cal-acts-empty">那天他没留下痕迹——大概就安安静静陪着你。</div>
          )}
        </div>

        {/* —— 翻月 —— */}
        <div className="cal-month">
          <button type="button" className="cal-arrow" onClick={() => go(-1)} disabled={cur <= FIRST} aria-label="上个月">
            <ChevronLeft size={22} />
          </button>
          <span className="cal-month-t">
            {ym.y !== ty && <small>{ym.y} 年 </small>}
            {ym.m} 月
          </span>
          <button type="button" className="cal-arrow" onClick={() => go(1)} disabled={cur >= now + 12} aria-label="下个月">
            <ChevronRight size={22} />
          </button>
        </div>

        {/* —— 一整个月 —— */}
        <div className="cal-grid">
          {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
            const o = looks[d] && looks[d].o;
            const fut = isFuture(d);
            const cls =
              'cal-cell' +
              (fut ? ' future' : '') +
              (d === sel ? ' sel' : '') +
              (cur === now && d === td ? ' today' : '') +
              (ANNIV[`${ym.m}-${d}`] ? ' anniv' : '');
            return (
              <button type="button" className={cls} key={d} onClick={() => setSel(d)}>
                <Svg className="cal-cell-crab" html={crabSvg(pool, o, { anim: false })} />
                <span className="cal-cell-n">{d}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
